import type { ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/HoverCard';
import { SnapshotChangePill } from '@/components/snapshot/SnapshotChangePill';
import { formatWorkspaceMetricValue } from '@/components/workspace/WorkspaceMetricCell';
import { cn } from '@/utils/cn';
import {
  resolveRetailKpiMetrics,
  type DerivedMetricTotals,
  type MetricPolarity,
  type WorkspaceMetricColumnDef,
  type WorkspaceRetailKpiMetrics,
} from '@/utils/workspaceAdProductMetrics';

export type WorkspaceKpiKey =
  | 'totalSales'
  | 'organicSales'
  | 'adSales'
  | 'totalOrders'
  | 'organicOrders'
  | 'adOrders'
  | 'tacos'
  | 'acos'
  | 'spend'
  | 'roas'
  | 'clicks'
  | 'ctr'
  | 'cpc'
  | 'cr'
  | 'impressions'
  | 'cpo';

type ResolvedRetail = ReturnType<typeof resolveRetailKpiMetrics>;

type KpiDef = {
  key: WorkspaceKpiKey;
  label: string;
  polarity: MetricPolarity;
  format: WorkspaceMetricColumnDef['format'];
  /** When true, card is shown on first Workspace load. */
  defaultVisible: boolean;
  tooltip?: { title: string; body: string };
  getValue: (ad: DerivedMetricTotals, retail: ResolvedRetail) => number;
};

export const WORKSPACE_KPI_DEFS: KpiDef[] = [
  {
    key: 'totalSales',
    label: 'Total Sales',
    polarity: 'higher_is_better',
    format: 'currency',
    defaultVisible: true,
    tooltip: {
      title: 'Total Sales',
      body: 'Vendor or seller retail sales for accounts linked to this Ads profile. Never below ad sales if retail days are missing. Falls back to ad sales until a retail account is mapped.',
    },
    getValue: (_ad, retail) => retail.totalSales,
  },
  {
    key: 'organicSales',
    label: 'Organic Sales',
    polarity: 'higher_is_better',
    format: 'currency',
    defaultVisible: true,
    tooltip: {
      title: 'Organic Sales',
      body: 'Retail sales not attributed to ads. Organic + Paid equals Total Sales. Total is the greater of vendor/seller retail and ad sales when retail days are incomplete.',
    },
    getValue: (_ad, retail) => retail.organicSales,
  },
  {
    key: 'adSales',
    label: 'Ad Sales',
    polarity: 'higher_is_better',
    format: 'currency',
    defaultVisible: true,
    getValue: (ad) => ad.sales30d,
  },
  {
    key: 'totalOrders',
    label: 'Total Orders',
    polarity: 'higher_is_better',
    format: 'int',
    defaultVisible: true,
    tooltip: {
      title: 'Total Orders',
      body: 'Ordered units from linked Seller or Vendor Central accounts. Falls back to ad orders until a retail account is mapped.',
    },
    getValue: (_ad, retail) => retail.totalOrders,
  },
  {
    key: 'organicOrders',
    label: 'Organic Orders',
    polarity: 'higher_is_better',
    format: 'int',
    defaultVisible: true,
    tooltip: {
      title: 'Organic Orders',
      body: 'Retail orders not attributed to ads. Organic + Paid equals Total Orders. Total is the greater of vendor/seller units and ad orders when retail days are incomplete.',
    },
    getValue: (_ad, retail) => retail.organicOrders,
  },
  {
    key: 'adOrders',
    label: 'Ad Orders',
    polarity: 'higher_is_better',
    format: 'int',
    defaultVisible: true,
    getValue: (ad) => ad.purchases30d,
  },
  {
    key: 'tacos',
    label: 'TACoS',
    polarity: 'lower_is_better',
    format: 'percent',
    defaultVisible: true,
    tooltip: {
      title: 'Total Advertising Cost of Sales',
      body: 'Ad spend ÷ total sales × 100. Total sales is Seller or Vendor Central ordered revenue when a retail account is linked.',
    },
    getValue: (_ad, retail) => retail.tacos,
  },
  {
    key: 'acos',
    label: 'ACoS',
    polarity: 'lower_is_better',
    format: 'percent',
    defaultVisible: true,
    tooltip: {
      title: 'Advertising Cost of Sales',
      body: 'Ad spend ÷ ad sales × 100.',
    },
    getValue: (ad) => ad.acos,
  },
  {
    key: 'spend',
    label: 'Spend',
    polarity: 'lower_is_better',
    format: 'currency',
    defaultVisible: true,
    getValue: (ad) => ad.cost,
  },
  {
    key: 'roas',
    label: 'RoAS',
    polarity: 'higher_is_better',
    format: 'ratio',
    defaultVisible: true,
    tooltip: {
      title: 'Return on Ad Spend',
      body: 'Ad sales ÷ ad spend.',
    },
    getValue: (ad) => ad.roas,
  },
  {
    key: 'clicks',
    label: 'Clicks',
    polarity: 'higher_is_better',
    format: 'int',
    defaultVisible: true,
    getValue: (ad) => ad.clicks,
  },
  {
    key: 'ctr',
    label: 'CTR',
    polarity: 'higher_is_better',
    format: 'percent',
    defaultVisible: true,
    tooltip: {
      title: 'Click-Through Rate',
      body: 'Clicks ÷ impressions × 100.',
    },
    getValue: (ad) => ad.ctr,
  },
  {
    key: 'cpc',
    label: 'CPC',
    polarity: 'lower_is_better',
    format: 'currency',
    defaultVisible: true,
    tooltip: {
      title: 'Cost Per Click',
      body: 'Ad spend ÷ clicks.',
    },
    getValue: (ad) => ad.cpc,
  },
  {
    key: 'cr',
    label: 'Conversion Rate',
    polarity: 'higher_is_better',
    format: 'percent',
    defaultVisible: true,
    getValue: (ad) => ad.cr,
  },
  {
    key: 'impressions',
    label: 'Impressions',
    polarity: 'higher_is_better',
    format: 'int',
    defaultVisible: false,
    getValue: (ad) => ad.impressions,
  },
  {
    key: 'cpo',
    label: 'CPO',
    polarity: 'lower_is_better',
    format: 'currency',
    defaultVisible: false,
    tooltip: {
      title: 'Cost Per Order',
      body: 'Ad spend ÷ ad orders.',
    },
    getValue: (ad) => ad.cpo,
  },
];

/** Stable default visibility for Performance Summary (14 of 16). */
export const DEFAULT_VISIBLE_WORKSPACE_KPI_KEYS: WorkspaceKpiKey[] = WORKSPACE_KPI_DEFS.filter(
  (def) => def.defaultVisible
).map((def) => def.key);

export function resolveVisibleWorkspaceKpiDefs(
  visibleKeys: readonly WorkspaceKpiKey[]
): KpiDef[] {
  const visible = new Set(visibleKeys);
  return WORKSPACE_KPI_DEFS.filter((def) => visible.has(def.key));
}

/**
 * Desktop (xl) column count for a two-row Performance Summary layout.
 * e.g. 14→7, 15→8, 16→8, 13→7, 12→6 — avoids an orphan third-row card.
 */
export function workspaceKpiXlColumnCount(visibleCount: number): number {
  if (visibleCount <= 0) return 1;
  return Math.ceil(visibleCount / 2);
}

const XL_GRID_COLS_CLASS: Record<number, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
  7: 'xl:grid-cols-7',
  8: 'xl:grid-cols-8',
};

export function workspaceKpiGridClassName(visibleCount: number): string {
  const xlCols = Math.min(8, workspaceKpiXlColumnCount(visibleCount));
  return cn(
    'grid grid-cols-2 auto-rows-fr gap-2.5 sm:grid-cols-4',
    XL_GRID_COLS_CLASS[xlCols] ?? 'xl:grid-cols-7'
  );
}

/** Fine-pointer hover only: scale the card itself so the grid does not reflow. */
const KPI_HOVER_LIFT =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:z-30 [@media(hover:hover)_and_(pointer:fine)]:hover:scale-[1.22] [@media(hover:hover)_and_(pointer:fine)]:hover:rounded-3xl [@media(hover:hover)_and_(pointer:fine)]:hover:shadow-[0_22px_48px_-12px_rgba(15,23,42,0.28),0_10px_20px_-10px_rgba(15,23,42,0.14)]';

const KPI_CARD_CLASS = cn(
  'relative z-0 flex h-full min-h-[7.25rem] w-full origin-center flex-col gap-1.5 overflow-hidden p-3',
  'transition-[transform,box-shadow,border-radius] duration-200 ease-out',
  KPI_HOVER_LIFT,
);

function asColumnDef(def: KpiDef): WorkspaceMetricColumnDef {
  return {
    key: def.key,
    label: def.label,
    polarity: def.polarity,
    format: def.format,
  };
}

function KpiLabel({ def }: { def: KpiDef }) {
  if (!def.tooltip) {
    return <p className="text-xs font-medium text-foreground-muted">{def.label}</p>;
  }

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="inline-flex max-w-full items-center gap-1 text-left text-xs font-medium text-foreground-muted hover:text-foreground"
        >
          <span>{def.label}</span>
          <HelpCircle className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" className="w-64 p-3">
        <p className="text-sm font-semibold text-foreground">{def.tooltip.title}</p>
        <p className="mt-1 text-xs text-foreground-muted">{def.tooltip.body}</p>
      </HoverCardContent>
    </HoverCard>
  );
}

function KpiTile({
  def,
  current,
  previous,
  retail,
  previousRetail,
  compareEnabled,
  isLoading,
}: {
  def: KpiDef;
  current: DerivedMetricTotals;
  previous: DerivedMetricTotals;
  retail: ResolvedRetail;
  previousRetail: ResolvedRetail;
  compareEnabled: boolean;
  isLoading: boolean;
}) {
  const column = asColumnDef(def);
  const cur = def.getValue(current, retail);
  const prev = def.getValue(previous, previousRetail);

  if (isLoading) {
    return (
      <Card className={KPI_CARD_CLASS} aria-hidden>
        <div className="h-3 w-16 animate-pulse rounded bg-surface-muted" />
        <div className="mt-1 h-6 w-24 animate-pulse rounded bg-surface-muted" />
        <div className="h-5 w-14 shrink-0 animate-pulse rounded-full bg-surface-muted" />
        <div className="ml-auto mt-1 h-5 w-16 animate-pulse rounded-full bg-surface-muted" />
      </Card>
    );
  }

  return (
    <Card className={KPI_CARD_CLASS} data-kpi={def.key}>
      <KpiLabel def={def} />

      <p className="truncate font-mono text-lg font-bold tabular-nums leading-tight text-foreground">
        {formatWorkspaceMetricValue(column, cur)}
      </p>

      {/* w-fit: flex-col stretch must not widen the compact change pill. */}
      <div className="w-fit max-w-full shrink-0">
        <SnapshotChangePill
          current={cur}
          previous={prev}
          polarity={def.polarity}
          show={compareEnabled}
        />
      </div>

      {compareEnabled ? (
        <div className="mt-1 flex w-full justify-end">
          <span className="inline-flex max-w-full truncate rounded-full bg-muted/70 px-2 py-0.5 text-[11px] font-medium text-foreground-muted">
            vs {formatWorkspaceMetricValue(column, prev)}
          </span>
        </div>
      ) : null}
    </Card>
  );
}

export function WorkspaceKpiSummary({
  current,
  previous,
  retail,
  previousRetail,
  compareEnabled,
  isLoading = false,
  isFiltered = false,
  rangeSubtitle,
  visibleKeys = DEFAULT_VISIBLE_WORKSPACE_KPI_KEYS,
  headerActions,
}: {
  current: DerivedMetricTotals;
  previous: DerivedMetricTotals;
  retail?: WorkspaceRetailKpiMetrics;
  previousRetail?: WorkspaceRetailKpiMetrics;
  compareEnabled: boolean;
  isLoading?: boolean;
  isFiltered?: boolean;
  rangeSubtitle?: string;
  visibleKeys?: readonly WorkspaceKpiKey[];
  headerActions?: ReactNode;
}) {
  const resolvedCurrent = resolveRetailKpiMetrics(current, retail);
  const resolvedPrevious = resolveRetailKpiMetrics(previous, previousRetail);
  const visibleDefs = resolveVisibleWorkspaceKpiDefs(visibleKeys);
  const subtitleParts = [rangeSubtitle].filter(Boolean) as string[];
  if (isFiltered) subtitleParts.push('Filtered to selected entities');

  return (
    <section aria-label="Workspace performance summary" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">Performance summary</h2>
        {headerActions || subtitleParts.length > 0 ? (
          <div className="flex flex-wrap items-center gap-3">
            {headerActions}
            {subtitleParts.length > 0 ? (
              <p className="text-body-sm text-foreground-muted">{subtitleParts.join(' · ')}</p>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={workspaceKpiGridClassName(visibleDefs.length)} role="list">
        {visibleDefs.map((def) => (
          <div key={def.key} role="listitem" className="relative min-w-0">
            <KpiTile
              def={def}
              current={current}
              previous={previous}
              retail={resolvedCurrent}
              previousRetail={resolvedPrevious}
              compareEnabled={compareEnabled}
              isLoading={isLoading}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export function WorkspaceKpiSummarySkeleton({
  visibleCount = DEFAULT_VISIBLE_WORKSPACE_KPI_KEYS.length,
}: {
  visibleCount?: number;
}) {
  return (
    <section aria-label="Loading performance summary" className="space-y-2">
      <div className="h-5 w-44 animate-pulse rounded bg-surface-muted" />
      <div className={workspaceKpiGridClassName(visibleCount)}>
        {Array.from({ length: visibleCount }, (_, index) => (
          <div key={index} className="relative min-w-0">
            <Card className={KPI_CARD_CLASS} aria-hidden>
              <div className="h-3 w-16 animate-pulse rounded bg-surface-muted" />
              <div className="mt-1 h-6 w-24 animate-pulse rounded bg-surface-muted" />
              <div className="h-5 w-14 shrink-0 animate-pulse rounded-full bg-surface-muted" />
              <div className="ml-auto mt-1 h-5 w-16 animate-pulse rounded-full bg-surface-muted" />
            </Card>
          </div>
        ))}
      </div>
    </section>
  );
}
